/**
 * Permission-Based UI Protection Components
 *
 * This module provides components for protecting UI elements based on user permissions.
 * These components allow for fine-grained control over what users can see and interact with.
 */

import React from "react";
import { AppPermission, AppRole } from "@/types/auth";
import { useAppAuth } from "@/contexts/AuthContext";

interface ProtectByPermissionProps {
  permission: AppPermission;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Component that conditionally renders children based on a single permission
 */
export function ProtectByPermission({
  permission,
  fallback = null,
  children,
}: ProtectByPermissionProps) {
  const { hasPermission } = useAppAuth();

  if (!hasPermission(permission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface ProtectByPermissionsProps {
  permissions: AppPermission[];
  requireAll?: boolean; // If true, requires ALL permissions; if false, requires ANY
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Component that conditionally renders children based on multiple permissions
 */
export function ProtectByPermissions({
  permissions,
  requireAll = false,
  fallback = null,
  children,
}: ProtectByPermissionsProps) {
  const { hasPermission, hasAnyPermission } = useAppAuth();

  const hasAccess = requireAll
    ? permissions.every((permission) => hasPermission(permission))
    : hasAnyPermission(permissions);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface ProtectByRoleProps {
  role: AppRole;
  minimumRole?: boolean; // If true, allows this role and higher
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Component that conditionally renders children based on user role
 */
export function ProtectByRole({
  role,
  minimumRole = false,
  fallback = null,
  children,
}: ProtectByRoleProps) {
  const { appRole } = useAppAuth();

  const roleHierarchy: Record<AppRole, number> = {
    owner: 4,
    admin: 3,
    manager: 2,
    member: 1,
  };

  const hasAccess = minimumRole ? roleHierarchy[appRole] >= roleHierarchy[role] : appRole === role;

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface ProtectByOwnershipProps {
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Component that only renders for organization owners
 */
export function ProtectByOwnership({ fallback = null, children }: ProtectByOwnershipProps) {
  const { isOwner } = useAppAuth();

  if (!isOwner) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

interface ConditionalRenderProps {
  condition: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Generic conditional render component
 */
export function ConditionalRender({
  condition,
  fallback = null,
  children,
}: ConditionalRenderProps) {
  if (!condition) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Hook for permission-based conditional rendering logic
 */
export function usePermissionRender() {
  const { hasPermission, hasAnyPermission, appRole, isOwner } = useAppAuth();

  const renderIfPermission =
    (permission: AppPermission, fallback?: React.ReactNode) => (children: React.ReactNode) =>
      hasPermission(permission) ? children : fallback;

  const renderIfAnyPermission =
    (permissions: AppPermission[], fallback?: React.ReactNode) => (children: React.ReactNode) =>
      hasAnyPermission(permissions) ? children : fallback;

  const renderIfRole =
    (role: AppRole, fallback?: React.ReactNode) => (children: React.ReactNode) =>
      appRole === role ? children : fallback;

  const renderIfOwner = (fallback?: React.ReactNode) => (children: React.ReactNode) =>
    isOwner ? children : fallback;

  return {
    renderIfPermission,
    renderIfAnyPermission,
    renderIfRole,
    renderIfOwner,
    hasPermission,
    hasAnyPermission,
    appRole,
    isOwner,
  };
}

/**
 * Higher-order component for permission-based protection
 */
export function withPermissionProtection<P extends object>(
  Component: React.ComponentType<P>,
  permission: AppPermission,
  fallback?: React.ReactNode
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectByPermission permission={permission} fallback={fallback}>
        <Component {...props} />
      </ProtectByPermission>
    );
  };
}

/**
 * Button component that's automatically disabled based on permissions
 */
interface PermissionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  permission: AppPermission;
  disabledText?: string;
  children: React.ReactNode;
}

export function PermissionButton({
  permission,
  disabledText = "No permission",
  children,
  className = "",
  ...props
}: PermissionButtonProps) {
  const { hasPermission } = useAppAuth();
  const canPerformAction = hasPermission(permission);

  return (
    <button
      {...props}
      disabled={!canPerformAction || props.disabled}
      className={`${className} ${!canPerformAction ? "cursor-not-allowed opacity-50" : ""}`}
      title={!canPerformAction ? disabledText : props.title}
    >
      {children}
    </button>
  );
}

/**
 * Link component that's automatically disabled based on permissions
 */
interface PermissionLinkProps {
  permission: AppPermission;
  href: string;
  className?: string;
  disabledClassName?: string;
  children: React.ReactNode;
}

export function PermissionLink({
  permission,
  href,
  className = "",
  disabledClassName = "opacity-50 cursor-not-allowed pointer-events-none",
  children,
}: PermissionLinkProps) {
  const { hasPermission } = useAppAuth();
  const canAccess = hasPermission(permission);

  if (!canAccess) {
    return <span className={`${className} ${disabledClassName}`}>{children}</span>;
  }

  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

/**
 * Menu item component that conditionally renders based on permissions
 */
interface PermissionMenuItemProps {
  permission: AppPermission;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function PermissionMenuItem({
  permission,
  href,
  onClick,
  icon,
  children,
  className = "flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100",
}: PermissionMenuItemProps) {
  const { hasPermission } = useAppAuth();

  if (!hasPermission(permission)) {
    return null;
  }

  const content = (
    <>
      {icon && <span className="mr-3">{icon}</span>}
      {children}
    </>
  );

  if (href) {
    return (
      <a href={href} className={className}>
        {content}
      </a>
    );
  }

  return (
    <button onClick={onClick} className={`${className} w-full text-left`}>
      {content}
    </button>
  );
}

/**
 * Debug component for showing user permissions (development only)
 */
export function PermissionDebugger() {
  const { permissions, appRole, isOwner, clerkRole } = useAppAuth();

  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 max-w-sm rounded-lg bg-black bg-opacity-75 p-4 text-xs text-white">
      <h4 className="mb-2 font-bold">🔐 Auth Debug</h4>
      <p>
        <strong>Clerk Role:</strong> {clerkRole}
      </p>
      <p>
        <strong>App Role:</strong> {appRole}
      </p>
      <p>
        <strong>Is Owner:</strong> {isOwner ? "Yes" : "No"}
      </p>
      <p>
        <strong>Permissions:</strong>
      </p>
      <ul className="max-h-32 list-inside list-disc overflow-y-auto">
        {permissions.map((permission, index) => (
          <li key={index}>{permission}</li>
        ))}
      </ul>
    </div>
  );
}
